import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';

import { UserActions } from '@core/account-data';
import type { User } from '@core/account-types';
import { createEffectFactory } from '@core/ngrx-legacy';

import { InterviewSchedulesCollectionHttpClient } from './collection.http-client';
import { InterviewSchedulesCollectionActions } from './collection.store';
import { InterviewSchedulesCollectionScopes } from './collection.types';

@Injectable({ providedIn: 'root' })
export class InterviewSchedulesCollectionEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(InterviewSchedulesCollectionHttpClient);

  public effects = createEffectFactory(InterviewSchedulesCollectionActions, this.actions$);

  public fetchCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewSchedulesCollectionActions.fetchCollection),
      exhaustMap(({ payload }) =>
        this.http.loadCollection(payload?.scope || InterviewSchedulesCollectionScopes.Main).pipe(
          switchMap(({ interviewSchedules, references }) => [
            UserActions.references({ payload: { data: <User[]>references } }),
            InterviewSchedulesCollectionActions.fetchCollectionSuccess({
              payload: { data: interviewSchedules, scope: payload?.scope },
            }),
          ]),
          catchError(error =>
            of(
              InterviewSchedulesCollectionActions.fetchCollectionFailed({
                payload: { error, scope: payload?.scope },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public fetchPaginatedCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewSchedulesCollectionActions.fetchPaginatedCollection),
      exhaustMap(({ payload }) =>
        this.http.loadPaginatedCollection(payload.params).pipe(
          map(res => ({
            data: res,
            scope: payload.scope,
          })),
          switchMap(res => [
            UserActions.references({ payload: { data: <User[]>res.data.references } }),
            InterviewSchedulesCollectionActions.fetchPaginatedCollectionSuccess({
              payload: res,
            }),
          ]),
          catchError(error =>
            of(
              InterviewSchedulesCollectionActions.fetchPaginatedCollectionFailed({
                payload: { error, scope: payload?.scope },
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
